import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,Radio,RadioGroup,Stack,TextField } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import { useMutation } from "@tanstack/react-query";
import React,{ useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from '../../../helpers/matomo';
import Checklists from "../../../service/Checklists";
import queryClient from "../../../service/query";
import { NewFormType } from "../../../types/checklist/types";

type props = {
  open: boolean;
  handleCloseModal: Function;
};

const formularioService = new Checklists();

export default function NovoFormulario({ open, handleCloseModal }: props) {
  const { t } = useTranslation('translation');
  const [nome, setNome] = useState<string>('');
  const [tipoPreenchimento, setTipoPreenchimento] = useState<boolean>(false);

  const navigate = useNavigate();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['CREATE_CHECKLIST'],
    mutationFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token não encontrado');
          if (token) {
            const itemData: NewFormType[] = [{
              nome: 'Item',
              peso: 1,
              subitens: [
                {
                  id: 0,
                  nome: 'Subitem',
                  tipo: 'avaliacao',
                  peso: 1,
                  threshold: -1,
                  avaliacao: [{
                    nome: "1",
                    valor: 0
                  },
                  {
                    nome: "2",
                    valor: 100
                  }]
                }
              ]
            }];
            const response = await formularioService.newForm(token, nome, itemData, true, tipoPreenchimento);
            if (!response) throw new Error('Erro ao criar novo modelo de formulário');
            return response;
          }
        }, {
          pending: 'Criando novo modelo de formulário...',
          success: 'Novo modelo de formulário criado com sucesso!',
          error: 'Erro ao criar novo modelo de formulário!'
        }, { toastId: 'custom.novo_modelo_criado_com_sucesso' });
        return promise;
      } catch (error) {
        throw new Error('Erro ao criar novo modelo de formulário');
      }
    },
    onSuccess: (response) => {
      trackEventMatomo('gestão de formulários', 'sucesso', 'criação', 'formulário criado');
      queryClient.refetchQueries({ queryKey: ['GET_INDEXES'] });
      handleCloseModal();
      routeChange(response.id);
    },
    onError: () => trackEventMatomo('gestão de formulários', 'erro', 'criação', 'falha ao criar formulário'),
  });

  async function routeChange(id: string) {
    navigate(`/formularios/${id}`);
  }

  function onChangeName(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    setNome(event.target.value);
  }

  function handleChangeTipoPreenchimento(event: React.ChangeEvent<HTMLInputElement>) {
    setTipoPreenchimento(event.target.value === 'true');
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={open}
      onClose={() => {
        trackEventMatomo('gestão de formulários', 'click', 'modal', 'fecha criação de formulário');
        handleCloseModal();
      }}
      scroll={'paper'}
    >
      <DialogTitle>{t('checklists.checklists.newFormTitle')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          trackEventMatomo('gestão de formulários', 'click', 'botão', 'fecha criação de formulário');
          handleCloseModal();
        }}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction='column' spacing={2} alignItems='stretch' justifyContent='flex-start' padding={2}>
          <Stack direction='row' justifyContent='center' alignItems='center' spacing={2}>
            <RadioGroup defaultValue={tipoPreenchimento} onChange={handleChangeTipoPreenchimento} row>
              <FormControlLabel value={false} control={<Radio />} label={t('checklists.checklists.loggedHierarchical')} />
              <FormControlLabel value={true} control={<Radio />} label={t('checklists.checklists.webAnonymous')} />
            </RadioGroup>
          </Stack>
          <TextField
            required
            slotProps={{ htmlInput: { maxLength: 200 } }}
            onChange={onChangeName}
            value={nome}
            label={t('checklists.checklists.modelName')}
            variant="outlined"
            fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={(!nome.length) || isPending} onClick={() => {
          trackEventMatomo('gestão de formulários', 'click', 'botão', 'cria formulário');
          mutateAsync();
        }}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
